import MapboxDraw, { DrawCustomMode, DrawCustomModeThis } from "@mapbox/mapbox-gl-draw";

import readOnlySelect from "fond/draw/modes/base/readOnlySelect";

import { buildEndpointIndex, EndpointIndex, findConnectedChain } from "./chainSelection";

const {
  constants: Constants,
  lib: {
    doubleClickZoom,
    CommonSelectors: { isFeature, isShiftDown },
  },
} = MapboxDraw;

interface SelectSidesState {
  endpointIndex: EndpointIndex;
  [key: string]: unknown;
}

/**
 * Runtime methods available on the draw mode `this` context but not
 * fully represented in the @mapbox/mapbox-gl-draw type definitions.
 */
interface DrawModeThis extends DrawCustomModeThis {
  deselect(id: string | number): void;
  stopExtendedInteractions(state: unknown): void;
}

// readOnlySelect typed with clickOnFeature available (inherited from simple_select at runtime)
type ReadOnlySelectWithClick = DrawCustomMode & {
  clickOnFeature?(this: DrawModeThis, state: SelectSidesState, event: MapboxDraw.MapMouseEvent): void;
};

/**
 * A custom draw mode that selects entire connected chains of LineStrings.
 * Clicking a LineString finds all transitively connected LineStrings
 * (via co-located endpoints) and selects them as a group.
 *
 * Shift-click on an unselected feature adds its chain to the selection.
 * Shift-click on a selected feature removes only that feature.
 * Box select expands each feature in the box to its full chain.
 */
// clickOnFeature is a runtime method from simple_select, not in DrawCustomMode types
const selectSides = {
  ...readOnlySelect,

  onSetup(this: DrawModeThis) {
    const parentState = (readOnlySelect as ReadOnlySelectWithClick).onSetup?.call(this as unknown as DrawCustomModeThis & DrawCustomMode, {}) ?? {};
    const allFeatures = this._ctx.api.getAll().features;
    return { ...(parentState as object), endpointIndex: buildEndpointIndex(allFeatures) } as SelectSidesState;
  },

  onClick(
    this: DrawModeThis & { clickOnFeature(state: SelectSidesState, event: MapboxDraw.MapMouseEvent): void },
    state: SelectSidesState,
    event: MapboxDraw.MapMouseEvent
  ) {
    if (isFeature(event)) {
      this.clickOnFeature(state, event);
      return;
    }
    // Click on empty space — clear selection and force full re-render
    this.clearSelectedFeatures();
    const store = this._ctx.store as unknown as { setDirty(): void; render(): void };
    store.setDirty();
    store.render();
    doubleClickZoom.enable(this);
  },

  onMouseUp(this: DrawModeThis, state: SelectSidesState, event: MapboxDraw.MapMouseEvent) {
    const wasBoxSelecting = (state as Record<string, unknown>).boxSelecting;

    // Let parent (simple_select) handle box select completion — it selects individual features in the box
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readOnlySelect.onMouseUp?.call(this as any, state, event);

    // After box select, expand each selected feature into its full chain
    if (wasBoxSelecting) {
      const selectedIds = this.getSelectedIds();
      const chainIds = new Set<string>();

      for (const id of selectedIds) {
        for (const chainId of findConnectedChain(state.endpointIndex, String(id))) {
          chainIds.add(chainId);
        }
      }

      for (const id of chainIds) {
        if (!this.isSelected(id)) {
          this.select(id);
        }
      }

      const store = this._ctx.store as unknown as { setDirty(): void; render(): void };
      store.setDirty();
      store.render();
    }
  },

  onTouchEnd(this: DrawModeThis, state: SelectSidesState, event: MapboxDraw.MapMouseEvent) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (selectSides as any).onMouseUp.call(this, state, event);
  },

  clickOnFeature(this: DrawModeThis, state: SelectSidesState, event: MapboxDraw.MapMouseEvent) {
    doubleClickZoom.disable(this);
    this.stopExtendedInteractions(state);

    const featureId = event.featureTarget.properties?.id;
    if (!featureId) return;

    const feature = this.getFeature(featureId);
    if (!feature || feature.type !== Constants.geojsonTypes.LINE_STRING) {
      (readOnlySelect as ReadOnlySelectWithClick).clickOnFeature?.call(this, state, event);
      return;
    }

    const chainIds = findConnectedChain(state.endpointIndex, String(featureId));
    if (chainIds.size === 0) return;

    const isShiftClick = isShiftDown(event);
    const isFeatureSelected = this.isSelected(featureId);

    if (isShiftClick && isFeatureSelected) {
      // Shift-click on selected: remove only the clicked feature
      this.deselect(featureId);
      if (this.getSelectedIds().length === 0) {
        doubleClickZoom.enable(this);
      }
    } else if (isShiftClick) {
      // Shift-click on unselected: add chain to selection
      for (const id of chainIds) {
        if (!this.isSelected(id)) {
          this.select(id);
        }
      }
    } else {
      // Regular click: replace selection with chain
      const chainArray = Array.from(chainIds);
      this.setSelected(chainArray[0]);
      for (let i = 1; i < chainArray.length; i++) {
        this.select(chainArray[i]);
      }
    }

    // Force a full re-render of all features so active/inactive styling updates correctly
    const store = this._ctx.store as unknown as { setDirty(): void; render(): void };
    store.setDirty();
    store.render();
  },
} as unknown as DrawCustomMode;

export default selectSides;
